import { z } from 'zod';

import { formatMB } from '@/utils/format/bytes';

import { SiteOperationalPhaseSchema } from './site-operational-phase';
import { UnitStatusSchema } from './site-unit-status';

export const SiteSchema = z.object({
  id: z.string(),
  fullFrontendId: z.string(),
  name: z.string(),
  address: z.string(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  pictureUrl: z.string().optional().nullable(),

  operationalPhase: SiteOperationalPhaseSchema,

  nominalPowerW: z.number().optional().nullable(),
  nominalEnergyWH: z.number().optional().nullable(),
  cellType: z.string().optional().nullable(),
  alerts: z.object({
    critical: z.number(),
    warning: z.number(),
  }),
  nbOpenTasks: z.number(),
  unitStatus: UnitStatusSchema,
  installationDate: z.date().optional(),
  commissioningDate: z.date().optional(),
});

export const SitePreviewSchema = SiteSchema.pick({
  id: true,
  fullFrontendId: true,
  name: true,
});

export const SitePreviewExtendedSchema = SiteSchema.pick({
  id: true,
  alerts: true,
  cellType: true,
  fullFrontendId: true,
  name: true,
  nbOpenTasks: true,
  nominalEnergyWH: true,
  nominalPowerW: true,
  operationalPhase: true,
}).merge(
  z.object({
    lng: z.number(),
    lat: z.number(),
    nbMissingDocuments: z.number(),
  }),
);

export const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png'];
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

const PictureSchema = z
  .instanceof(File, { message: 'Required field' })
  .refine(
    (file) => {
      return !file || file.size <= MAX_FILE_SIZE;
    },
    `File size must be less than ${formatMB(MAX_FILE_SIZE)}`,
  )
  .refine((file) => {
    return ALLOWED_FILE_TYPES.includes(file.type);
  }, 'File type not supported');

export const SiteUpdateSchema = SiteSchema.pick({
  id: true,
}).merge(
  z.object({
    picture: PictureSchema.optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    address: z.string().optional(),
    installationDate: z.date().optional(),
    commissioningDate: z.date().optional(),
    operationalPhase: SiteOperationalPhaseSchema.optional(),
    nominalPowerW: z.number().optional(),
    nominalEnergyWh: z.number().optional(),
  }),
);

export const SiteCreateSchema = z.object({
  name: z.string().min(1),
  operationalPhase: SiteOperationalPhaseSchema,
  address: z.string().min(1),
  lat: z.number(),
  lng: z.number(),
  nominalEnergyWh: z.number(),
  nominalPowerW: z.number(),
  picture: PictureSchema.optional(),
  installationDate: z.date(),
  commissioningDate: z.date(),
});

export type Site = z.infer<typeof SiteSchema>;
export type SitePreviewExtended = z.infer<typeof SitePreviewExtendedSchema>;
export type SitePreview = z.infer<typeof SitePreviewSchema>;
export type SiteUpdate = z.infer<typeof SiteUpdateSchema>;
export type SiteCreate = z.infer<typeof SiteCreateSchema>;
